/**
 * Invite Friends Modal styles – theme-aware for dark mode.
 */

import { StyleSheet } from 'react-native';
import type { ThemeColors } from '../../theme/themes';
import { getTheme } from '../../theme/getTheme';

export function getInviteModalStyles(theme: ThemeColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: theme.background.overlay,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      padding: 20,
    },
    container: {
      backgroundColor: theme.background.card,
      borderRadius: 24,
      padding: 24,
      width: '100%' as const,
      maxWidth: 500,
      maxHeight: '80%',
      flexDirection: 'column' as const,
    },
    header: {
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
      alignItems: 'center' as const,
      marginBottom: 20,
    },
    headerTitle: {
      fontSize: 22,
      fontWeight: 'bold' as const,
      color: theme.gray[900],
    },
    closeButton: {
      fontSize: 24,
      color: theme.gray[600],
    },
    closeButtonPressed: { opacity: 0.7 },
    closeButtonHover: { opacity: 0.9 },
    tabContainer: {
      flexDirection: 'row' as const,
      marginBottom: 20,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[300],
    },
    tab: {
      flex: 1,
      paddingBottom: 12,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    tabActive: {
      borderBottomColor: theme.primary,
    },
    tabPressed: { opacity: 0.8 },
    tabHover: { opacity: 0.9 },
    tabText: {
      fontSize: 16,
      fontWeight: '400' as const,
      color: theme.gray[600],
      textAlign: 'center' as const,
    },
    tabTextActive: {
      fontWeight: '600' as const,
      color: theme.primary,
    },
    scrollContent: {
      maxHeight: 400,
      marginBottom: 20,
    },
    searchInput: {
      backgroundColor: theme.gray[100],
      borderRadius: 8,
      padding: 12,
      marginBottom: 16,
      fontSize: 14,
      color: theme.gray[900],
    },
    connectSection: {
      padding: 20,
      alignItems: 'center' as const,
    },
    connectText: {
      color: theme.gray[600],
      fontSize: 14,
      marginBottom: 16,
      textAlign: 'center' as const,
    },
    connectButton: {
      backgroundColor: theme.primary,
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 8,
    },
    connectButtonDisabled: {
      backgroundColor: theme.gray[400],
      opacity: 0.6,
    },
    connectButtonText: {
      color: theme.white,
      fontSize: 16,
      fontWeight: '600' as const,
    },
    configWarning: {
      color: theme.gray[500],
      fontSize: 12,
      marginTop: 8,
      textAlign: 'center' as const,
    },
    loadingContainer: {
      padding: 20,
      alignItems: 'center' as const,
    },
    loadingText: {
      color: theme.gray[600],
      fontSize: 14,
      marginTop: 12,
    },
    emptyContainer: {
      padding: 20,
      alignItems: 'center' as const,
    },
    emptyText: {
      color: theme.gray[600],
      fontSize: 14,
    },
    contactItem: {
      backgroundColor: theme.gray[100],
      borderRadius: 12,
      padding: 12,
      marginBottom: 8,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
    },
    contactItemSelected: {
      borderWidth: 2,
      borderColor: theme.primary,
    },
    contactItemDisabled: {
      opacity: 0.6,
    },
    contactItemPressed: { opacity: 0.8 },
    contactItemHover: { opacity: 0.9 },
    contactAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: theme.primary + '20',
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      marginRight: 12,
    },
    contactAvatarText: {
      fontSize: 18,
    },
    contactInfo: {
      flex: 1,
    },
    contactName: {
      fontSize: 16,
      fontWeight: '600' as const,
      color: theme.gray[900],
    },
    contactEmail: {
      fontSize: 14,
      color: theme.gray[600],
    },
    contactStatus: {
      fontSize: 12,
      color: theme.gray[500],
      fontStyle: 'italic' as const,
      marginTop: 2,
    },
    checkmark: {
      fontSize: 24,
      color: theme.primary,
    },
    manualEmailRow: {
      flexDirection: 'row' as const,
      marginBottom: 16,
      gap: 8,
    },
    manualEmailInput: {
      flex: 1,
      backgroundColor: theme.gray[100],
      borderRadius: 12,
      padding: 12,
      fontSize: 16,
      color: theme.gray[900],
    },
    addButton: {
      backgroundColor: theme.primary,
      paddingHorizontal: 20,
      paddingVertical: 12,
      borderRadius: 12,
      justifyContent: 'center' as const,
    },
    addButtonText: {
      color: theme.white,
      fontWeight: '600' as const,
      fontSize: 16,
    },
    addButtonPressed: { opacity: 0.7 },
    addButtonHover: { opacity: 0.9 },
    manualEmailLabel: {
      fontSize: 12,
      color: theme.gray[500],
    },
    removeButton: {
      padding: 8,
    },
    removeButtonPressed: { opacity: 0.7 },
    removeButtonHover: { opacity: 0.9 },
    removeButtonText: {
      fontSize: 20,
      color: theme.error,
    },
    messageContainer: {
      marginBottom: 20,
    },
    messageLabel: {
      fontSize: 14,
      fontWeight: '600' as const,
      color: theme.gray[900],
      marginBottom: 8,
    },
    messageInput: {
      backgroundColor: theme.gray[100],
      borderRadius: 12,
      padding: 12,
      color: theme.gray[900],
      fontSize: 14,
      minHeight: 80,
      textAlignVertical: 'top' as const,
    },
    sendButton: {
      backgroundColor: theme.primary,
      paddingVertical: 16,
      borderRadius: 12,
      alignItems: 'center' as const,
      marginTop: 20,
    },
    sendButtonDisabled: {
      backgroundColor: theme.gray[400],
      opacity: 0.6,
    },
    sendButtonPressed: { opacity: 0.7 },
    sendButtonHover: { opacity: 0.9 },
    sendButtonText: {
      color: theme.white,
      fontSize: 16,
      fontWeight: '600' as const,
    },
  });
}

export type InviteModalStyles = ReturnType<typeof getInviteModalStyles>;

/** Backward compatibility – default to light theme. Prefer useInviteModalStyles() inside InviteFriendsModal tree for theme-aware styles. */
export const inviteModalStyles = getInviteModalStyles(getTheme('light'));
